<template>
  <div class="dd-sidebar">
    <!-- Mobile: label + caret opens a mobile-friendly navigation list -->
    <dropdown
      v-if="$screen.isMobile($mq)"
      ref="dropdownRef"
      :title="title"
      class="dd-sidebar__mobile-picker"
      popper-min-width-size="large"
    >
      <!-- Label -->
      <div class="label">
        <template v-if="activeOption">
          <i :class="'ddi-' + activeOption.icon" class="ddi title-icon" />
          <span class="title">{{ activeOption.label }}</span>
        </template>
        <span v-else class="title">{{ title }}</span>
        <i class="ddi ddi-caret-down" />
      </div>
      <!-- Navigation list -->
      <template #content>
        <div v-for="group in elements" :key="group.label">
          <h5>{{ group.label }}</h5>
          <ul>
            <li
              v-for="option in group.options"
              :key="option.value"
              @click="goToRoute(option.value)"
            >
              <i :class="'ddi-' + option.icon" class="ddi" />
              <span :class="{ 'selected': option.isActive }">{{ option.label }}</span>
            </li>
          </ul>
        </div>
      </template>
    </dropdown>

    <!-- Desktop: full navigation list -->
    <div v-else class="dd-sidebar__options-list">
      <div v-for="group in elements" :key="group.label">
        <h5>{{ group.label }}</h5>
        <div
          v-for="option in group.options"
          :key="option.value"
          :class="{ 'active' : option.isActive }"
          class="dd-sidebar__option"
          @click="goToRoute(option.value)"
        >
          <i :class="'ddi-' + option.icon" class="ddi" />
          <span>{{ option.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { INavMenuGroup, INavMenuItem } from '@/types/data-objects/global'

export default defineComponent({
  name: 'DdSidebar',

  props: {
    elements: {
      type: Array as () => INavMenuGroup[],
      required: true,
    },
    /* Title shown on the mobile navigation sheet (and as the label when nothing is active) */
    title: {
      type: String,
      default: 'Settings',
    },
  },

  computed: {
    activeOption(): INavMenuItem | null {
      for (const group of this.elements) {
        const active = group.options.find(o => o.isActive)
        if (active) {
          return active
        }
      }
      return null
    },
  },

  methods: {
    goToRoute(value: string): void {
      const dropdown = this.$refs.dropdownRef as any
      if (dropdown) {
        dropdown.handleClose()
      }
      this.$router.push({ name: value })
    },
  },
})
</script>

<style lang="scss">
.dd-sidebar {
  h5 {
    @include lettering--h5;
    padding: 10px 0 0 7px;
  }

  &__option {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    height: 32px;
    padding: 5px 12px;
    margin-bottom: 4px;

    > i {
      margin: 0 8px 0 -4px !important;
      color: $color--gray-60 !important;
    }

    > span {
      @include lettering--p;
      @include single-line-ellipsis;
      font-weight: inherit;
    }

    &:hover {
      background-color: rgba($color--gray-60, 0.12);
    }

    &.active {
      font-weight: $bold;
      background-color: var(--color-text-primary);

      > * {
        color: $color--white !important;
      }
    }
  }

  &__mobile-picker {
    @include single-line-ellipsis;
    max-width: 100%;
    /* Line up with the page heading and card headings, which sit 16px in on mobile */
    padding: 0 16px;

    &:hover {
      .title {
        border-bottom: $border--solid-medium;
      }
    }

    .label {
      @include single-line-ellipsis;
      @include lettering--h4;
      color: var(--color-text-secondary);
      font-weight: 500;

      .title {
        font-weight: 500;
      }

      .title-icon {
        top: -1px;
        right: -1px;
        margin-right: 6px;
        color: var(--color-text-third);
      }

      .ddi-caret-down {
        margin-left: 6px;
        font-size: 10px;
        top: -1px;
      }
    }
  }
}

html[data-theme='dark'] {
  .dd-sidebar__option {
    &:hover {
      background-color: var(--color-background-hover);
    }

    &.active {
      background-color: var(--color-background-third);

      > * {
        color: var(--color-text-secondary) !important;
      }
    }
  }
}
</style>
